import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { BusyIndicatorService, SnackBarService } from '../../Busy-Indicator';
import { RestrictService, Roles } from '../../Common/Permissions/restrict.service';
import { ActionViewCategoryTypes } from '../../constants.new';
import {
    ActionViewCategoryInput,
    ActionViewRepository,
    ActionViewSavedSearchPatch,
} from '../../Core-Repositories/actionView.repository';
import { UserInstanceService } from '../../User/userInstance.service';
import { HttpCancelableRequest } from '../../Common/Data/base.repository';
import {
    ActionViewFilterColumn,
    getActionViewFieldId,
    prepareActionViewFilters,
    prepareActionViewSavedFilters,
    toActionViewOutputColumn,
} from './actionView.adapters';
import {
    ActionViewSavedCriteria,
    ActionViewSavedSearch,
    ActionViewSearchRequest,
    ActionViewSearchResult,
    ActionViewTaskType,
    ActionViewTaskTypeOptions,
    ActionViewUrgency,
    TaskActionViewIncludeOption,
    TaskActionViewShowOption,
    TaskActionViewThatAreOption,
    TaskActionViewUrgencyOption,
    TaskActionViewWhereOption,
} from './actionView.models';

export interface ActionViewChoice<T extends number> {
    id: T;
    display: string;
}

export interface ActionViewAssignmentOptions {
    showOptions: Readonly<Record<string, ActionViewChoice<TaskActionViewShowOption>>>;
    whereOptions: ReadonlyArray<ActionViewChoice<TaskActionViewWhereOption>>;
    thatAreOptions: ReadonlyArray<ActionViewChoice<TaskActionViewThatAreOption>>;
    includeOptions: ReadonlyArray<ActionViewChoice<TaskActionViewIncludeOption>>;
}

@Injectable({ providedIn: 'root' })
export class ActionViewApiService {
    constructor(
        private readonly repository: ActionViewRepository,
        private readonly restrictService: RestrictService,
        private readonly userInstanceService: UserInstanceService,
        private readonly busyIndicatorService: BusyIndicatorService,
        private readonly snackBarService: SnackBarService,
    ) {}

    private activeSearch: HttpCancelableRequest<ActionViewSearchResult> | null = null;

    async getTaskTypes(): Promise<ActionViewTaskTypeOptions> {
        const result = await lastValueFrom(this.repository.getTaskTypes());
        const isRyanInstance = this.userInstanceService.isCurrentInstanceRyan();
        const hasRyanPrivate =
            this.restrictService.isInRole(Roles.RYANPRIVATEITEMSVIEW) ||
            this.restrictService.isInRole(Roles.RYANPRIVATEITEMSEDIT);
        const hasInstancePrivatePermission = this.restrictService.hasAnyInstancePrivate();

        return {
            ...result,
            taskTypes: result.taskTypes.filter(
                (taskType) =>
                    ((isRyanInstance && hasRyanPrivate) ||
                        taskType.taskSeriesTypeID !== Core.TaskSeriesTypes.ClientInvoice) &&
                    (hasInstancePrivatePermission ||
                        taskType.taskSeriesTypeID !== Core.TaskSeriesTypes.DocumentIntake) &&
                    (isRyanInstance || taskType.taskSeriesTypeID !== Core.TaskSeriesTypes.PaymentBatch),
            ),
        };
    }

    sortTaskTypes(taskTypes: ActionViewTaskType[]): ActionViewTaskType[] {
        return [...taskTypes].sort(
            (left, right) =>
                left.taskSeriesTypeSequence - right.taskSeriesTypeSequence || left.sequence - right.sequence,
        );
    }

    async executeActionView(
        criteria: ActionViewSearchRequest,
        savedSearchId: number | null = null,
    ): Promise<ActionViewSearchResult | null> {
        this.cancelSearch();
        const request: ActionViewSearchRequest = {
            ...structuredClone(criteria),
            savedSearchID: savedSearchId ?? criteria.savedSearchID ?? null,
        };
        const search = this.repository.search(request);
        this.activeSearch = search;
        const result = await search.execute();
        if (this.activeSearch === search) {
            this.activeSearch = null;
        }
        if (search.isCanceled || !result.completed) {
            return null;
        }
        return result.value;
    }

    cancelSearch(): void {
        this.activeSearch?.cancel();
        this.activeSearch = null;
    }

    prepareRequest(
        criteria: ActionViewSearchRequest,
        filterColumns: ActionViewFilterColumn[],
    ): ActionViewSearchRequest {
        return {
            ...structuredClone(criteria),
            filters: prepareActionViewFilters(filterColumns).filter((filter) => filter.or.length > 0),
            taskOutputColumns: criteria.taskOutputColumns.map((column) => toActionViewOutputColumn(column)),
            entityOutputColumns: criteria.entityOutputColumns.map((column) => toActionViewOutputColumn(column)),
            showUserTeams: criteria.showUserTeams.filter((selection) => Boolean(selection.userID)),
            showTeams: criteria.showTeams.filter((selection) => Number.isInteger(selection.teamID)),
            includeUserTeams: criteria.includeUserTeams.filter((selection) => Boolean(selection.userID)),
            includeTeams: criteria.includeTeams.filter((selection) => Number.isInteger(selection.teamID)),
        };
    }

    async startExcelExport(criteria: ActionViewSearchRequest): Promise<void> {
        const busyRef = this.busyIndicatorService.show({ message: 'Exporting search results' });
        try {
            const request = {
                ...structuredClone(criteria),
                outputInt: 1,
                outputType: Core.AdvancedSearchRequestOutputTypes.Excel,
            };
            const processId = await lastValueFrom(this.repository.startExport(request));
            this.snackBarService.addById(processId, Compliance.LongRunningProcessTypeEnum.ActionViewExport);
        } finally {
            busyRef.hide();
        }
    }

    getAssignmentOptions(singleInstanceSelected: boolean): ActionViewAssignmentOptions {
        return {
            showOptions: {
                myTasks: { id: TaskActionViewShowOption.MyTasks, display: 'My Tasks' },
                myTeamsTasks: {
                    id: TaskActionViewShowOption.MyTeamsTasks,
                    display: singleInstanceSelected ? "My Team's Tasks" : "My Teams' Tasks",
                },
                individualsTasks: {
                    id: TaskActionViewShowOption.SpecificIndividualsTasks,
                    display: "Specific Individual's Tasks",
                },
                teamsTasks: {
                    id: TaskActionViewShowOption.SpecificTeamsTasks,
                    display: "Specific Team's Tasks",
                },
                allTasks: { id: TaskActionViewShowOption.AllTasks, display: 'All Tasks' },
            },
            whereOptions: [
                { id: TaskActionViewWhereOption.Assigned, display: 'Assigned' },
                { id: TaskActionViewWhereOption.UltimatelyResponsible, display: 'Ultimately Responsible' },
                {
                    id: TaskActionViewWhereOption.AssignedOrUltimatelyResponsible,
                    display: 'Assigned or Ultimately Responsible',
                },
                { id: TaskActionViewWhereOption.AccountHandler, display: 'Account Handler' },
                {
                    id: TaskActionViewWhereOption.ComplianceReturnsPPResp,
                    display: 'Compliance Returns PP Resp',
                },
            ],
            thatAreOptions: [
                { id: TaskActionViewThatAreOption.Ready, display: 'Ready' },
                { id: TaskActionViewThatAreOption.NotReady, display: 'Not Ready' },
                { id: TaskActionViewThatAreOption.ReadyOrNotReady, display: 'Ready or Not Ready' },
                { id: TaskActionViewThatAreOption.Completed, display: 'Completed' },
            ],
            includeOptions: [
                { id: TaskActionViewIncludeOption.AllCompleted, display: 'All Completed Tasks' },
                { id: TaskActionViewIncludeOption.CompletedByMe, display: 'Tasks Completed by Me' },
                {
                    id: TaskActionViewIncludeOption.CompletedBySpecificIndividual,
                    display: 'Tasks Completed by a Specific Individual',
                },
            ],
        };
    }

    getUrgencies(): ActionViewUrgency[] {
        return [
            { id: TaskActionViewUrgencyOption.Overdue, display: 'Overdue (Black)' },
            { id: TaskActionViewUrgencyOption.Critical, display: 'Critical (Red)' },
            { id: TaskActionViewUrgencyOption.Imminent, display: 'Imminent (Yellow)' },
            { id: TaskActionViewUrgencyOption.Approaching, display: 'Approaching (Green)' },
            { id: TaskActionViewUrgencyOption.Prospective, display: 'Prospective (Grey)' },
            { id: TaskActionViewUrgencyOption.CriticalOrImminent, display: 'Critical Or Imminent' },
        ];
    }

    getUrgencyColumnStyleClass(sequence: number): string {
        return (
            ['urgency-overdue', 'urgency-critical', 'urgency-imminent', 'urgency-approaching', 'urgency-prospective'][
                sequence
            ] ?? ''
        );
    }

    getUrgencyChoiceByHeaderName(header: string): TaskActionViewUrgencyOption {
        const urgency = this.getUrgencies().find(
            (item) => item.display.split(' ')[0].toLowerCase() === header.toLowerCase(),
        );
        if (!urgency) {
            throw new Error(`Unknown Action View urgency header: ${header}`);
        }
        return urgency.id;
    }

    getCategories(): Promise<Core.ActionViewSavedSearchCategoryManagementDTO[]> {
        return lastValueFrom(this.repository.getCategories());
    }

    createCategory(category: ActionViewCategoryInput): Promise<Core.SavedSearchCategoryManagementDTO> {
        return lastValueFrom(this.repository.createCategory(category));
    }

    getCustomActionViewById(actionViewId: number): Promise<ActionViewSavedSearch> {
        return lastValueFrom(this.repository.getCustomActionView(actionViewId));
    }

    getSystemActionViewById(actionViewId: number): Promise<ActionViewSavedSearch> {
        return lastValueFrom(this.repository.getSystemActionView(actionViewId));
    }

    createActionView(actionView: ActionViewSavedSearch, type: ActionViewCategoryTypes): Promise<ActionViewSavedSearch> {
        return lastValueFrom(
            type === ActionViewCategoryTypes.SYSTEM
                ? this.repository.createSystemActionView(actionView)
                : this.repository.createCustomActionView(actionView),
        );
    }

    updateActionView(actionView: ActionViewSavedSearch, type: ActionViewCategoryTypes): Promise<ActionViewSavedSearch> {
        return lastValueFrom(
            type === ActionViewCategoryTypes.SYSTEM
                ? this.repository.updateSystemActionView(actionView)
                : this.repository.updateCustomActionView(actionView),
        );
    }

    patchActionView(
        actionViewId: number,
        patch: ActionViewSavedSearchPatch,
        type: ActionViewCategoryTypes,
    ): Promise<ActionViewSavedSearch> {
        return lastValueFrom(
            type === ActionViewCategoryTypes.SYSTEM
                ? this.repository.patchSystemActionView(actionViewId, patch)
                : this.repository.patchCustomActionView(actionViewId, patch),
        );
    }

    async toggleFavorite(actionViewId: number, favorite: boolean): Promise<void> {
        if (favorite) {
            await lastValueFrom(this.repository.addFavorite(actionViewId));
        } else {
            await lastValueFrom(this.repository.deleteFavorite(actionViewId));
        }
    }

    getFavoriteCount(actionViewId: number): Promise<number> {
        return lastValueFrom(this.repository.getFavoriteCount(actionViewId));
    }

    async share(actionView: Core.ActionViewListDTO): Promise<void> {
        await lastValueFrom(this.repository.share(actionView));
    }

    async revokeShare(actionView: ActionViewSavedSearchPatch): Promise<void> {
        await lastValueFrom(this.repository.revokeShare(actionView));
    }

    toSavedCriteria(
        criteria: ActionViewSearchRequest,
        filterColumns: ActionViewFilterColumn[],
    ): ActionViewSavedCriteria {
        const filters = prepareActionViewSavedFilters(filterColumns);
        return {
            outputColumns: filterColumns.map(getActionViewFieldId),
            filters,
            masterFilter: structuredClone(criteria),
        };
    }
}
